/**
 * Reuse a signed storage URL until shortly before it expires.
 * A missing path is signed with the others. A deleted path is dropped.
 */

const cache = new Map();
const MAX_ENTRIES = 400;

function cacheKey(bucket, path) {
  return `${bucket}:${path}`;
}

function clearSignedUrlCache() {
  cache.clear();
}

function dropCachedSignedUrl(bucket, path) {
  if (!path) return;
  cache.delete(cacheKey(bucket, path));
}

function readCached(bucket, path, now) {
  const key = cacheKey(bucket, path);
  const hit = cache.get(key);
  if (!hit || hit.expiresAt <= now) {
    if (hit) cache.delete(key);
    return '';
  }
  return hit.signedUrl;
}

function writeCached(bucket, path, signedUrl, seconds, now) {
  if (!path || !signedUrl) return;
  const ttlMs = Math.max(30, Number(seconds) - 120) * 1000;
  cache.set(cacheKey(bucket, path), { signedUrl, expiresAt: now + ttlMs });
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
}

/**
 * createSignedUrls is (paths, seconds) => { data, error }.
 * Returned data lines up with paths. Cached paths are not sent again.
 */
async function signCached(bucket, paths, seconds, createSignedUrls, now = Date.now()) {
  const list = paths || [];
  const signed = new Array(list.length);
  const missing = [];
  const missingAt = [];
  for (let index = 0; index < list.length; index += 1) {
    const hit = readCached(bucket, list[index], now);
    if (hit) signed[index] = { signedUrl: hit };
    else {
      missing.push(list[index]);
      missingAt.push(index);
    }
  }
  if (missing.length === 0) return { data: signed, error: null };
  const result = await createSignedUrls(missing, seconds);
  if (result && result.error) return { data: null, error: result.error };
  const rows = (result && result.data) || [];
  for (let index = 0; index < missing.length; index += 1) {
    const row = rows[index] || null;
    signed[missingAt[index]] = row;
    if (row && row.signedUrl && !row.error) writeCached(bucket, missing[index], row.signedUrl, seconds, now);
  }
  return { data: signed, error: null };
}

module.exports = {
  clearSignedUrlCache,
  dropCachedSignedUrl,
  signCached,
};
